import clsx from "clsx";
import { type FC, useEffect, useMemo } from "react";

import { AddCourseCodeGroupingModal } from "./AddCourseGroupingModal";
import { AddCourseCodeModal } from "./AddCourseModal";
import { CourseCodeNavListItem } from "./CourseNavListItem";

import {
  COURSE_CODE_GROUPING_ID_QUERY_PARAM,
  COURSE_CODE_ID_QUERY_PARAM,
} from "components/constants";
import {
  type CourseViewOption,
  CourseViewOptionMenu,
} from "components/menus/SectionViewOptionMenu";
import { CourseType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { showConfirmationModal } from "mds/components/Modal";
import { TextAddButton } from "mds/components/TextAddButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { FolderIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import {
  selectCourseCodeGroupingsWithCodes,
  selectCourseCodesWithGrouping,
  selectUngroupedCourseCodes,
} from "store/selectors/section";
import { useDnDItemOrdering, useLocalCopy } from "utils/collections";
import { useQueryParamsManager } from "utils/routing";

const COURSE_CODE_EVENT_PREFIX = "Home - Course Catalog / ";

type CourseNavListProps = {
  canEdit: boolean;
  onOptionChange: (option: CourseViewOption) => void;
};

export const CourseCodeNavList: FC<CourseNavListProps> = ({ canEdit, onOptionChange }) => {
  const groupingsWithCodes = useAppSelector(selectCourseCodeGroupingsWithCodes);
  const ungroupedCourseCodes = useAppSelector(selectUngroupedCourseCodes);
  const courseCodesWithGrouping = useAppSelector(selectCourseCodesWithGrouping);
  const { setQueryParams, getQueryParam } = useQueryParamsManager();
  const hasCourseCodes = groupingsWithCodes.length > 0 || ungroupedCourseCodes.length > 0;
  const courseCodeId = getQueryParam(COURSE_CODE_ID_QUERY_PARAM);
  const groupingId = getQueryParam(COURSE_CODE_GROUPING_ID_QUERY_PARAM);
  const isSmallScreen = !useIsMdOrLarger();

  // Collect all course codes (from groupings and ungrouped) for finding current selection
  const allCourseCodes = useMemo(
    () => [
      ...groupingsWithCodes.flatMap((grouping) => grouping.courseCodes),
      ...ungroupedCourseCodes,
    ],
    [groupingsWithCodes, ungroupedCourseCodes],
  );
  const currentGrouping = groupingsWithCodes.find((grouping) => grouping.id === groupingId);
  const currentCourseCode = allCourseCodes.find((code) => code.id === courseCodeId);
  const firstGroupId = groupingsWithCodes[0]?.id;
  const firstCourseCodeId = ungroupedCourseCodes[0]?.id;
  const [localCourseCodes, setLocalCourseCodes] = useLocalCopy(allCourseCodes);

  const moveItemsLocally = useDnDItemOrdering(localCourseCodes, setLocalCourseCodes);

  const onDropItem = (droppedItem: CourseType) => {
    storeApi.course_codes.partial_update({ id: droppedItem.id, order: droppedItem.order });
  };

  // Set default query params if neither grouping_id nor course_code_id are set
  useEffect(() => {
    if (!groupingId && !courseCodeId) {
      if (firstGroupId) {
        // Set the first grouping if there are any groupings
        setQueryParams({
          [COURSE_CODE_GROUPING_ID_QUERY_PARAM]: firstGroupId,
          [COURSE_CODE_ID_QUERY_PARAM]: null,
        });
      } else if (firstCourseCodeId) {
        // Set the first ungrouped course code if there are no groupings
        setQueryParams({
          [COURSE_CODE_ID_QUERY_PARAM]: firstCourseCodeId,
          [COURSE_CODE_GROUPING_ID_QUERY_PARAM]: null,
        });
      }
    }
    // eslint-disable-next-line no-object-deps/no-object-deps
  }, [groupingId, courseCodeId, firstGroupId, firstCourseCodeId, setQueryParams]);

  const handleCreateCourseCode = () => {
    showConfirmationModal({
      title: t("home.course_codes.add_course_code_title"),
      hideCancelConfirmButtons: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      children: (onClose) => (
        <AddCourseCodeModal
          eventName={`${COURSE_CODE_EVENT_PREFIX} Add Course Code`}
          localCourseCodes={localCourseCodes}
          onClose={onClose}
        />
      ),
    });
  };

  const handleCreateCourseCodeGrouping = () => {
    showConfirmationModal({
      title: t("home.course_codes.add_course_code_grouping_title"),
      hideCancelConfirmButtons: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      children: (onClose) => (
        <AddCourseCodeGroupingModal
          courseCodes={courseCodesWithGrouping}
          eventName={`${COURSE_CODE_EVENT_PREFIX} Add Course Code Grouping`}
          groupingsWithCodes={groupingsWithCodes}
          onClose={onClose}
        />
      ),
    });
  };

  return (
    <div
      className={clsx(
        "grid max-h-[calc(100%-24px)] w-[240px] grid-rows-[auto_1fr_auto] rounded-2xl border border-black-tint-90 p-4",
        isSmallScreen && "w-full",
      )}
      role="list"
    >
      <div className="mb-4">
        <CourseViewOptionMenu
          eventPrefix={COURSE_CODE_EVENT_PREFIX}
          selectedOption="course_code"
          onOptionChange={onOptionChange}
        />
      </div>

      <div className="min-h-0 overflow-y-auto">
        {/* Render groupings with their course codes */}
        {groupingsWithCodes.map((grouping) => {
          const isSelected = currentGrouping?.id === grouping.id;
          const isExpanded = isSelected || currentCourseCode?.grouping_id === grouping.id;
          const localGroupingCodes = localCourseCodes.filter(
            (code) => code.grouping_id === grouping.id,
          );

          return (
            <div key={grouping.id}>
              {/* Grouping header */}
              <div
                className="my-1 flex min-w-12 cursor-pointer items-center justify-between rounded-lg px-2 hover:bg-black-tint-97 data-[selected]:border-blue-tint-40 data-[selected]:bg-blue data-[selected]:text-white"
                data-selected={isSelected || undefined}
                onClick={() =>
                  setQueryParams({
                    [COURSE_CODE_GROUPING_ID_QUERY_PARAM]: grouping.id,
                    [COURSE_CODE_ID_QUERY_PARAM]: null,
                  })
                }
              >
                <div className="flex items-center gap-2">
                  <FolderIcon />

                  <h3 className="body-s my-1 w-full min-w-12 truncate font-medium">
                    {grouping.title}
                  </h3>
                </div>
              </div>

              {/* Course codes within grouping (indented when expanded) */}
              {isExpanded &&
                localGroupingCodes.map((courseCode, index) => (
                  <CourseCodeNavListItem
                    className="ml-6"
                    courseCode={courseCode}
                    dropIndex={index}
                    isSelected={!currentGrouping && currentCourseCode?.id === courseCode.id}
                    key={courseCode.id}
                    onDropItem={onDropItem}
                    onMoveLocally={moveItemsLocally}
                  />
                ))}
            </div>
          );
        })}

        {/* Render ungrouped course codes */}
        {ungroupedCourseCodes.map((courseCode, index) => {
          const localCode = localCourseCodes.find((c) => c.id === courseCode.id);
          if (!localCode) return null;

          return (
            <CourseCodeNavListItem
              courseCode={courseCode}
              dropIndex={index}
              isSelected={!currentGrouping && currentCourseCode?.id === courseCode.id}
              key={courseCode.id}
              onDropItem={onDropItem}
              onMoveLocally={moveItemsLocally}
            />
          );
        })}
      </div>

      {canEdit && (
        <>
          <TextAddButton
            className={clsx("w-[206px] max-w-[206px]", hasCourseCodes && "mt-2")}
            eventName={`${COURSE_CODE_EVENT_PREFIX}${t("common.add.course_code")}`}
            kind="gray"
            size="s"
            title={t("common.add.course_code")}
            onClick={() => handleCreateCourseCode()}
          >
            <span className="font-medium text-black-tint-20">{t("common.add.course_code")}</span>
          </TextAddButton>

          <Button
            className="mt-2 w-[206px] max-w-[206px]"
            eventName={`${COURSE_CODE_EVENT_PREFIX}${t("common.add.course_code_grouping")}`}
            kind="gray"
            size="s"
            title={t("common.add.course_code_grouping")}
            onClick={() => handleCreateCourseCodeGrouping()}
          >
            <IconText
              className="font-medium text-black-tint-20"
              iconStart={<FolderIcon />}
              text={t("common.add.course_code_grouping")}
            />
          </Button>
        </>
      )}
    </div>
  );
};
